/**
 * @file dcp/src/dcp-client/worker/supervisor2/sandbox2.js
 *
 * A sandbox that when constructed and assigned can do work for
 * a distributed slice. A sandbox runs for a single slice at a time.
 *
 * Usage (simplified...):
 *  const sandbox = new Sandbox(this, { ...this.options.sandboxOptions });
 *  await sandbox.start();
 *  await sandbox.assign(slice);
 *  return sandbox.work()
 *    .then((result) => {
 *      slice.collectResult(result, true);
 *      sandbox.checkSandboxReUse();
 *      this.supervisor.recordResult(slice)
 *    })
 *    .catch((error) => {
 *      slice.collectResult(error, false);
 *      const reason = this.supervisor.handleSandboxWorkError(sandbox, slice, error);
 *      this.supervisor.returnSlice(slice, reason);
 *      this.returnSandbox(sandbox);
 *    });
 *
 * Debug flags:
 *  Sandbox.debugWork = true // - turns off 30 second timeout to let user debug sandbox innards more easily
 *  Sandbox.debugState = true // - logs all state transitions for this sandbox
 *  Sandbox.debugEvents = true // - logs all events received from the sandbox
 *
 * Initial states:
 * UNREADY
 *
 * Terminal states:
 * TERMINATED
 *
 * Valid transitions:
 * ( sandbox.start )
 * UNREADY -> READYING -> READY
 *            READYING -> TERMINATED
 * ( sandbox.assign )
 * READY -> ASSIGNING -> ASSIGNED
 *          ASSIGNING -> TERMINATED
 * ( sandbox.markAsReserved )
 * ASSIGNED -> RESERVED
 * ( sandbox.markAsWorking )
 * RESERVED -> WORKING
 * ( sandbox.work )
 * WORKING -> ASSIGNED
 *         -> TERMINATED
 * ( sandbox.terminate )
 * any -> TERMINATED
 *
 * @author Matthew Palma, mpalma@kingsds.network
 *         Ryan Rossiter, ryan@kingsds.network
 *         Wes Garland, wes@distributive.network
 *         Paul, paul@distributive.network
 * @date May 2019
 *       May 2019
 *       Decemeber 2020
 *       June, Dec 2022, Jan-May 2023
 * @module sandbox
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

/**
 * In some situations we suppress emitting worker 'error' events when the sandbox is terminated.
 * It's possible this could hide a real error, so it's healthy to run CI sometimes while making such
 * errors noisy.
 */
const makeTerminatedErrorsNoisy = false;

const debugging = require('dcp/debugging').scope('worker');
const dcp_timers = require('dcp/common/dcp-timers');
const { assert, assertEq3 } = require('dcp/common/dcp-assert');
const { Synchronizer } = require('dcp/common/concurrency');
const { DCPError } = require('dcp/common/dcp-error');
const nanoid = require('nanoid').nanoid;
const EventEmitter = require('events');
const kvin = require("kvin");
const kvinv8 = require('kvin-v8');
const { stringify, a$sleepMs } = require('dcp/utils');
const common = require('./common');
const { selectiveDebug, timeDilation, selectiveDebug2, selectiveSupEx } = common;
const { Diagnostics } = require('./diagnostics');
const { capabilities } = require('dcp/common/scheduler-constants');

/**
 * Wraps console.debug to emulate debug module prefixing messages on npm.
 * @param {...any} args
 */
const debug = (...args) => {
  if (debugging())
    console.debug('Sandbox:', ...args);
};

// Sandbox states
const UNREADY    = 'UNREADY'    // No Sandbox (web worker, saworker, etc) has been constructed yet
const READYING   = 'READYING'   // Sandbox is being constructed and environment (bravojs, env) is being set up
const READY      = 'READY'      // Sandbox is ready to be assigned
const ASSIGNING  = 'ASSIGNING'  // Sandbox is in the process of being ASSIGNED
const ASSIGNED   = 'ASSIGNED'   // Sandbox is assigned but not working
const RESERVED   = 'RESERVED'   // Sandbox has been paired with a slice to execute, but sandbox.work() has not been called yet.
const WORKING    = 'WORKING'    // Sandbox is working -- sandbox.work() has been called
const TERMINATED = 'TERMINATED' // Sandbox is terminated.
const EVAL_RESULT_PREFIX = 'evalResult::';

class SandboxError extends Error
{
  /**
   * @param {string} errorCode
   * @param {string|Error} error
   */
  constructor (errorCode, error, name)
  {
    super(typeof error === 'string' ? error : error.message);

    /** @type {string} */
    this.errorCode = errorCode;
    if (typeof error !== 'string')
    {
      const pds = Object.getOwnPropertyDescriptors(error);
      for (let propName in pds)
        pds[propName].enumerable = true
      Object.defineProperties(this, pds);
      //this.name = name; Don't change the original name such as 'TypeError'; CI test check for this.
      this.stack = this.stack.replace(/(^DCPError)|(^Error)/, name);
    }
  }
}
class NoProgressError        extends SandboxError { constructor(error) { super('ENOPROGRESS',   error, 'NoProgressError'); } }
class SliceTooSlowError      extends SandboxError { constructor(error) { super('ESLICETOOSLOW', error, 'SliceTooSlowError'); } }
class UncaughtExceptionError extends SandboxError { constructor(error) { super('EUNCAUGHT',     error, 'UncaughtExceptionError'); } }

/** @typedef {import('./slice2').Slice} Slice                                */
/** @typedef {import('./config').Config} Config                              */
/** @typedef {import('./index').Supervisor} Supervisor                       */
/** @typedef {import('./job-manager').JobManager} JobManager                 */
/** @typedef {import('./module-cache').ModuleCache} ModuleCache              */
/** @typedef {import('dcp/utils/jsdoc-types').SandboxOptions} SandboxOptions */
/** @typedef {import('./sandbox-backoff-manager').SandboxBackoffManager} SandboxBackoffManager */

/**
 * Public event emitter.
 * Make data public so it works with introspection.
 * https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
 */
class SandboxHandle extends EventEmitter
{
  /**
   * @constructor
   * @param {Sandbox} sandbox
   */
  constructor (sandbox)
  {
    super({ captureRejections: false });
    for (let prop of Object.keys(this)) /* hide enumerable own props created by super */
    {
      Object.defineProperty(this, prop, {
        value:        this[prop],
        writable:     true,
        configurable: true,
        enumerable:   false,
      });
    }
    /** @type {{ id: number, public: { name: string, description: string, link: string }, jobManager: JobManager, slice: Slice }} */
    this.info = sandbox.info;
    /** @type {number} */
    this.id = this.info.id;
  }
  /** @type {{ name: string, description: string, link: string }} */
  get public ()      { return this.info.public ?? { name: '<unassigned>', description: '', link: '' }; }
  /** @type {string} */
  get jobId ()       { return this.info.jobManager?.jobId; }
  /** @type {number} */
  get sliceNumber () { return this.info.slice?.sliceNumber ?? -1; }
}
exports.SandboxHandle = SandboxHandle;

//
// Index to functionality -- search for '_Idx' to toggle through the index.
//
// 1)  class Sandbox
// 2)  stateSet, checkSandboxReUse, markAsReserved, markAsWorking, postMessageToEvaluator,
//     and punctuatedTimer is experimental for replacing hard-coded timeouts.
// 3)  start, describe, assign, applyRequirements, assignEvaluator
// 4)  eval, resetState, work, resetProgressTimeout, resetSliceTimeout
// 5)  handleRing0Message, handleRing1Message, handleRing2Message, handleRing3Message
// 6)  onmessage, onerror, terminate
// 7)  updateTime, resetSliceReport, error, warning
//

// _Idx
//
// class Sandbox
//

class Sandbox extends EventEmitter
{
  /**
   * A Sandbox (i.e. a worker sandbox) which executes distributed slices.
   *
   * @constructor
   * @param {Supervisor} supervisor
   * @param {SandboxOptions} options
   */
  constructor (supervisor, options)
  {
    super({ captureRejections: false });

    /** @type {{ id: number, public: { name: string, description: string, link: string }, jobManager: JobManager, slice: Slice }} */
    this.info = {
      id:         Sandbox.getNewId(),
      jobManager: null,
      slice:      null,
      public:     null,
    };

    /** @type {Synchronizer} */
    this.state = new Synchronizer(UNREADY, [ UNREADY, READYING, READY, ASSIGNING, ASSIGNED, RESERVED, WORKING, TERMINATED ]);

    /** @type {Supervisor} */
    this.supervisor = supervisor;
    /** @type {number} */
    this.gpuBarrier = 0;

    /** @type {ModuleCache} */
    this.moduleCache = supervisor.moduleCache;
    /** @type {SandboxOptions} */
    this.options = options;

    /**
     * Event emitter containing info that describes the sandbox.
     * @type {SandboxHandle}
     */
    this.sandboxHandle = new SandboxHandle(this);

    /** Properties of type object. */
    this.evaluatorHandle = null;
    this.capabilities = null;
    this.progressTimer = null;
    this.sliceTimeout = null;
    this.describeTimeout = null;
    this.requirementTimeout = null;
    this.resetStateTimeout = null;
    this.rejectionData = null;
    
    /**
     * Represents whether the tab entered a suspended state since the last progress event fired.
     * When the timer in resetProgressTimeout fires and this variable is true, ENOPROGRESS will be
     * supressed.  When resetProgressTimeout is called again this variable will be reset to the
     * current value of this.supervisor.detectedTabSuspended.
     * @type {boolean}
     */
    this.tabSuspendedSinceLastProgressEvent = false;
    /** @type {number?} */
    this.progress = 100;
    /** @type {{ last: { deltaMs: number, value: any, throttledReports: number }, lastDeterministic: { deltaMs: number, progress: number, value: any, throttledReports: number } }} */
    this.progressReports = null; // cf. job-noProgress.js

    /** @type {{ total: number, CPU: number, webGL: number, webGPU: number }} */
    this.sliceTimeReport = {
      total: 0,
      CPU: 0,
      webGL: 0,
      webGPU: 0,
    };
    /** @type {number} */
    this.moduleInDataSize = 0; // Sandbox-level input size; set during assign, never reset.
    /** @type {number} */
    this.sliceOutDataSize = 0; // Slice-level output size; reset for every slice executed.
    /** @type {number} */
    this.fetchInDataSize = 0; // Fetch input data byte measurement; reset for every slice executed.
    /** @type {number} */
    this.fetchOutDataSize = 0; // Fetch output data byte measurement; reset for every slice executed.

    /** @type {number} */
    this.useCounter = 1; // Anticipating the initial use.
    /** @type {Config} */
    this.hive = this.supervisor.options;
    /** @type {boolean} */
    this.endEventFired = false; // True when evaluatorHandle 'end' event has fired.

    this.ringMessageHandlers = [
      this.handleRing0Message,
      this.handleRing1Message,
      this.handleRing2Message,
      this.handleRing3Message,
    ];
  }

  /** @type {number} */
  get id ()         { return this.info.id; }
  /** @type {{ name: string, description: string, link: string }} */
  get public ()     { return this.info.public; }
  /** @type {{ name: string, description: string, link: string }} */
  set public (data) { this.info.public = data; }
  /** @type {JobManager} */
  get jobManager () { return this.info.jobManager; }
  /** @type {string} */
  get jobId ()      { return this.jobManager?.jobId; }
  /** @type {Slice} */
  get slice ()      { return this.info.slice; }
  /** @type {Slice} */
  set slice (slice) { this.info.slice = slice; }
  /** @type {number} */
  get sliceNumber () { return this.slice?.sliceNumber ?? -1; }
  /** @type {number} */
  get sliceStartTime () { return this.slice?.startTime || 0; }
  /** @type {number} */
  get generalTimeoutMs () { return 2 * this.hive.generalTimeoutMs * timeDilation; }
  /** @type {number} */
  get describeTimeoutMs () { return 3 * this.hive.generalTimeoutMs * timeDilation; }
  /** @type {number} */
  get punctuatedTimeoutMs () { return this.hive.generalTimeoutMs * timeDilation; }
  /** @type {number} */
  get inDataSize () { return this.moduleInDataSize + this.jobManager.inputDataSize + this.slice.inputDataSize + this.fetchInDataSize; }
  /** @type {number} */
  get outDataSize () { return this.sliceOutDataSize + this.fetchOutDataSize; }
  /** @type {SandboxBackoffManager} */
  get backoffManager () { return this.supervisor.sandboxBackoffManager; }

  /**
   * Debug string that characterizes sandbox.
   * @type {string}
   */
  get identifier()
  {
    if (!this.jobId)
      return `${this.id}.${this.state}`;
    if (this.slice)
      return `${this.id}.${this.jobId}.${this.state}~${this.slice.sliceNumber}.${this.slice.state}`;
    return `${this.id}.${this.jobId}.${this.state}`;
  }

  /** @returns {number} */
  static getNewId() { return Sandbox.idCounter++; }

  /** @type {boolean} */
  get isReady ()      { return this.state.is(READY); }
  /** @type {boolean} */
  get isAssigned ()   { return this.state.is(ASSIGNED); }
  /** @type {boolean} */
  get isReserved ()   { return this.state.is(RESERVED); }
  /** @type {boolean} */
  get isWorking ()    { return this.state.is(WORKING); }
  /** @type {boolean} */
  get isTerminated () { return this.state.is(TERMINATED); }

  // _Idx
  //
  // stateSet, checkSandboxReUse, markAsReserved, markAsWorking, postMessageToEvaluator,
  // punctuatedTimer is expiremental for replacing hard-coded timeouts.
  //

  /**
   * Cleanly handles the possibility that the evaluator handle 'end' event just fired and
   * caused this sandbox to be terminated. Using code = 'ESANDBOX' causes the Supervisor handling of
   * errors to not emit a worker 'error' event.
   *
   * @param {string} from
   * @param {string} to
   */
  stateSet (from, to)
  {
    if (this.isTerminated)
      throw new DCPError(`Sandbox ${this.identifier} has been terminated by evaluator handle 'end' event`, makeTerminatedErrorsNoisy ? 'ESANDBOX' : undefined);
    this.state.set(from, to);
  }

  /**
   * Mark WORKING sandbox as ASSIGNED in preparation for possible reuse.
   * Allow use of sandbox on a given job up to a limit of dcpConfig.supervisor.sandbox.maxSandboxUse .
   */
  checkSandboxReUse ()
  {
    selectiveDebug2() && console.debug(`Sandbox2.checkSandboxReUse: useCounter ${this.useCounter}, ${this.identifier}`);
    if (this.useCounter++ < this.hive.maxSandboxUse)
    {
      this.stateSet(WORKING, ASSIGNED);
      this.sandboxHandle.emit('ready');
    }
    else
    {
      this.terminate(false);
    }
  }

  /**
   * ASSIGNED --> RESERVED.
   * @returns {Sandbox}
   */
  markAsReserved ()
  {
    this.stateSet(ASSIGNED, RESERVED);
    return this;
  }
  
  /** RESERVED --> WORKING. */
  markAsWorking () { this.stateSet(RESERVED, WORKING); }

  /**
   * Safely post message to evaluator. When sandbox is terminated, throw an error with code = 'ESANDBOX'
   * which causes the Supervisor handling of errors to not emit a worker 'error' event.
   * @param {object}  message
   * @param {boolean} [recoverSlice]
   */
  postMessageToEvaluator (message, recoverSlice = false)
  {
    if (this.isTerminated) // When evaluator goes down, all sandboxes are terminated.
    {
      if (recoverSlice && this.slice && this.slice.tryRecoverSlice())
        this.slice = null; // Disconnect slice from sandbox so error handling won't return it to scheduler.
      throw new DCPError(`postMessageToEvaluator(${recoverSlice}): Sandbox ${this.identifier} has been terminated.`, makeTerminatedErrorsNoisy ? 'ESANDBOX' : undefined);
    }
    return this.evaluatorHandle.postMessage(message);
  }

  /**
   * Return slice to scheduler.
   * Should usually have some reason to believe it's not a good idea to try it again on the same worker.
   * @param {string|{message: string, errorCode: string}} reason
   */
  returnSlice (reason)
  {
    selectiveDebug() && console.debug('Sandbox.returnSlice', this.identifier);
    if (this.slice)
      return this.supervisor.returnSlice(this.slice, reason);
  }

  /**
   * @callback cbFn
   * @returns {void}
   */

  /**
   * UNUSED.
   * Future work.
   * Replaces the timers in:
   *   describe,
   *   applyRequirements,
   *   resetState,
   * The idea is to have a long timeout with a warning every
   * 6 seconds saying why it is waiting.
   * @param {cbFn} body
   * @param {string} waitMessage
   * @param {string} timerExpiredMessage
   * @returns {Promise<{ closeIntervalTimer: cbFn }>}
   */
  punctuatedTimer(body, waitMessage, timerExpiredMessage)
  {
    return new Promise((resolve, reject) => {
      let intervalCounter = 0;
      let intervalHandle = null;
      function closeIntervalTimer()
      {
        intervalHandle = Sandbox.clearTimer(intervalHandle);
      }
      intervalHandle = dcp_timers.setInterval(() => {
        if (++intervalCounter > 12)
        {
          closeIntervalTimer();
          this.supervisor.directError(timerExpiredMessage);
        }
        this.warning(waitMessage);
        body();
      }, this.punctuatedTimeoutMs)
      // Allow workers and localExec to exit.
      intervalHandle.unref();
      resolve({ closeIntervalTimer });
    });
  }

  // _Idx
  //
  // start, describe, assign, applyRequirements, assignEvaluator
  //

  /**
   * Readies the sandbox. This will result in the sandbox being ready and not assigned.
   * It will need to be assigned with a job before it is able to do work.
   * Sandbox.start will terminate the sandbox upon failure.
   * @todo maybe preload specific modules or let the cache pass in what modules to load?
   *
   * @returns {Promise<*>}
   * @throws on failure to ready
   */
  async start ()
  {
    // Do we really need this delay?
    // The delay was to prevent errors that happened when sandbox processing ocurred too quickly.
    // I (pfr) never saw them personally (very fast machine) but Eddie told me it was problem to be aware of.
    await this.supervisor.delayManager.nextDelay('sandboxStart');

    selectiveDebug2() && console.debug('Sandbox.start begin', this.identifier, this.supervisor.sandboxInventory.map((sbx) => sbx.identifier), Date.now()-this.supervisor.lastTime);
    try
    {
      // sandbox.terminate cannot begin executing in this critical region.
      // Instead all sandboxes that try to call sandbox.terminate(false) are placed in an array and
      // in the finally block of sandbox.start, we terminate them.
      this.backoffManager.createCriticalRegion = true;

      // Spin for at most ~250ms waiting for sandbox.terminate to complete.
      for (let k = 0; this.backoffManager.terminateCriticalRegion && k < 25; k++)
        a$sleepMs(10);
      // Waited long enough...if we're still in terminateCriticalRegion, go for it anyway.

      this.stateSet(UNREADY, READYING);

      const that = this;
      // RING 0
      this.evaluatorHandle = new this.supervisor.SandboxConstructor({
        name: `DCP Sandbox #${this.id}`,
      });
      // Annoying! onerror terminates sandbox which can happen independent of whether the slice
      // is ok or not. Since we don't know, we have to return the slice when onerror is called
      // during sandbox.work .
      /** @todo XXXpfr Beware of onerror firing. */
      this.evaluatorHandle.onerror = this.onerror.bind(this);

      const messageHandler = this.onmessage.bind(this);
      this.evaluatorHandle.onmessage = function onmessage(event)
      {
        var data;
        try
        {
          data = (event.data.serialized) ? kvin.parse(event.data.message) : kvin.unmarshal(event.data);
        }
        catch (e)
        {
          that.evaluatorHandle.onmessage = () => {}; // refuse to listen to any future messages
          if (that.isWorking) // sandbox is working -> workError
          {
            const error = 'Unserializable message from work function';
            messageHandler({ data: { ringSource: 3, value: { request: 'workError', error: new Error(error) } } });
            setImmediate(() => that.terminate(false));
          }
          else // sandbox isn't working -> sandboxError
          {
            that.emit('sandboxError', e);
            that.rejectWithCleanup('sandbox error while parsing message from evaluator', e);
            setImmediate(() => that.terminate(false));
          }
          return;
        }
        messageHandler({ data });
      }

      const evaluatorPostMessage = this.evaluatorHandle.postMessage.bind(this.evaluatorHandle);
      this.evaluatorHandle.postMessage = function postMessage(message)
      {
        evaluatorPostMessage(kvin.marshal(message));
      }

      this.evaluatorHandle.addEventListener('end', function sandbox$start$addEventListener() {
        selectiveDebug() && console.debug('Sandbox evaluatorHandle end-handler', that.identifier, that.supervisor.sandboxInventory.map((sbx) => sbx.identifier), Date.now()-that.supervisor.lastTime);
        that.endEventFired = true;
        // ESANDBOX is used to tune diagnostics, so that eval closure won't be too noisy
        const sn = that.slice ? `, sliceNumber ${that.sliceNumber}` : '';
        const ja = that.jobId ? `, job ${that.jobId}` : '';
        const customError = new DCPError(`Evaluator for sandbox ${that.id}${sn}${ja} has closed`, 'ESANDBOX');
        that.terminate(customError);
        // Notify slice scheduling logic that evaluatorHandle 'end' event has fired.
        that.supervisor.evaluatorEnd(that);
      });

      // Don't let an open socket prevent clean worker exit.
      // Is this needed anymore?
      if (this.evaluatorHandle.unref)
        this.evaluatorHandle.unref();

      // Now in RING 1

      // Now in RING 2
      await this.describe();
      this.stateSet(READYING, READY);

      // Emit the 'sandbox' event on the worker event emitter.
      this.supervisor.worker.emit('sandbox', this.sandboxHandle);

      // Clear any backoff delay.
      this.backoffManager.resetIndex(); // Success.

      selectiveDebug2() && console.debug('Sandbox.start end', this.identifier, this.supervisor.sandboxInventory.map((sbx) => sbx.identifier), Date.now()-this.supervisor.lastTime);
    }
    catch (error)
    {
      if (this.isTerminated)
        debug(`Failed to start sandbox because it is already terminated: ${this.identifier}.\n\tMay be due to evaluator throttling or death.`);
      else
      {
        debug(`Failed to start sandbox ${this.identifier}.`, error.message);
        // Enable or increase backoff delay.
        this.backoffManager.incrementIndex(); // Failure.
        this.terminate(false);
      }
      throw error;
    }
    finally
    {
      this.backoffManager.createCriticalRegion = false;
      if (this.backoffManager.pendingTerminate.length > 0)
        this.backoffManager.pendingTerminate.forEach((sbx) => sbx.terminate(false));
    }
  }

  /**
   * Sends a post message to describe its capabilities.
   * Side effect: Sets the capabilities property of the current sandbox.
   *
   * @returns {Promise<any>} Resolves with the sandbox's capabilities.
   * Rejects with an error saying a response was not received.
   * @memberof Sandbox
   */
  describe ()
  {
    debugging('sandbox') && console.debug('Beginning to describe evaluator', this.identifier);
    const that = this;

    return new Promise(function sandbox$describePromise(resolve, reject) {
      if (that.isTerminated) // When evaluator goes down, all sandboxes are terminated.
        reject(new Error(`Sandbox ${that.identifier} has been terminated.`));

      if (that.evaluatorHandle === null)
        reject(new Error(`Evaluator has not been initialized: ${that.identifier}`));

      function sandbox$describe$success(data)
      {
        that.off('reject', sandbox$describe$fail);
        dcp_timers.clearTimeout(that.describeTimeout);

        if (!that.supervisor.worktimes)
          that.supervisor.worktimes = data.worktimes;

        const capabilities = data.capabilities;
        if (typeof capabilities === 'undefined')
          reject(new Error(`Did not receive capabilities from describe response: ${that.identifier}`));
        // Is there any need to do structuredClone on capabilities?
        that.capabilities = capabilities;
        selectiveDebug2() && console.debug('Evaluator has been described', stringify(capabilities, -1, 2));
        resolve(capabilities);
      }
      function sandbox$describe$fail(error)
      {
        that.off('describe', sandbox$describe$success);
        dcp_timers.clearTimeout(that.describeTimeout);
        selectiveDebug() && console.debug('describe failed', that.identifier, error);
        reject(error);
      }
      // Emitted by handleRing2Message.
      that.once('describe', sandbox$describe$success);
      that.once('reject', sandbox$describe$fail);

      that.describeTimeout = dcp_timers.setTimeout(function sandbox$describe$timeout() {
        that.off('describe', sandbox$describe$success);
        that.off('reject', sandbox$describe$fail);
        reject(new Error( `Describe message timed-out. No describe response was received from the describe command: ${that.identifier}`));
      }, that.describeTimeoutMs);
      // Allow workers and localExec to exit.
      that.describeTimeout.unref();

      const message = {
        request: 'describe',
      };
      that.postMessageToEvaluator(message);
    });
  }

  /**
   * This will assign the sandbox with the job slice.jobId, loading code and data into the
   * evaluator sandbox.
   * @param {Slice} slice - The slice where slice.jobId is the job being assigned.
   * @returns {Promise<Sandbox>}
   * @throws on initialization failure
   */
  async assign (slice)
  {
    debugging('sandbox') && console.debug('Sandbox.assign begin', this.identifier);
    /**
     * Assigning a job to a sandbox may cause errors that need to be communicated to the job client.
     * This uses the event router workEmit protocol which requires validation of a security token
     * which is attached to every slice: authorizationMessage. This security token is necessary for
     * DCP daemons to allow a request to be processed.
     */
    this.slice = slice;

    await this.supervisor.delayManager.nextDelay('sandboxAssign');
    debug('Sandbox.assign', this.identifier, Date.now() - this.supervisor.lastTime);

    try
    {
      this.stateSet(READY, ASSIGNING);

      this.info.jobManager = slice.jobManager;
      this.job = this.jobManager.resolveWorktime();

      // At this point, the worker has decided that this sandbox will be associated with a specific job.
      // Therefore, we emit the SandboxHandle<job> event.
      this.sandboxHandle.emit('job', this.jobManager.jobHandle);

      assertEq3(this.job.jobId, this.jobId);
      assert(typeof this.job === 'object');
      assert(Array.isArray(this.job.dependencies));
      assert(Array.isArray(this.job.requirePath));

      // Extract public data from job, with defaults
      this.public = Object.assign({
        name:        `Anonymous Job ${this.jobId}`,
        description: 'Discreetly helping make the world smarter.',
        link:        'https://distributed.computer/about',
      }, this.jobManager.public);

      // Important: The order of applying requirements before loading the sandbox code
      // is important for modules and sandbox code to set globals over the whitelist.
      await this.applyRequirements();
      //const _t0 = Date.now();
      await this.assignEvaluator();
      //console.log('Finished Sandbox.assignEvaluator', Date.now() - _t0);
      this.stateSet(ASSIGNING, ASSIGNED);

      this.sandboxHandle.emit('ready');
      // Clear the assign failure count.
      this.jobManager.consecutiveSandboxAssignFailures = 0;
    }
    catch (error)
    {
      if (this.isTerminated)
        debug(`Failed to assign sandbox ${this.identifier} to evaluator because it is already terminated.\n\tMay be due to evaluator throttling or death.`);
      else
      {
        debug(`Failed to assign sandbox ${this.identifier} to evaluator.`);
        // Count assign failures and when too many, reject the job for the current worker.
        this.jobManager.consecutiveSandboxAssignFailures++;
        this.terminate(false);
      }
      throw error;
    }

    return this;
  }

  /**
   * Passes needed capabilities that modify the available symbols of the sandbox
   * to update the access list accordingly
   * E.g. disallow access to OffscreenCanvas without environment.offscreenCanvas=true present.
   * Must be called after @start.
   *
   * @returns {Promise<void>} - resolves on success, rejects otherwise
   */
  applyRequirements ()
  {
    debugging('sandbox') && console.debug('Sandbox.applyRequirements', this.identifier);
    const that = this;

    return new Promise(function sandbox$applyRequirementsPromise(resolve, reject) {
      function sandbox$applyRequirements$success()
      {
        that.off('reject', sandbox$applyRequirements$fail);
        dcp_timers.clearTimeout(that.requirementTimeout);
        resolve();
      }
      function sandbox$applyRequirements$fail(error)
      {
        that.off('applyRequirementsDone', sandbox$applyRequirements$success);
        dcp_timers.clearTimeout(that.requirementTimeout);
        selectiveDebug() && console.debug('applyRequirements failed', that.identifier, error);
        reject(error);
      }
      // Emitted by handleRing1Message.
      that.once('applyRequirementsDone', sandbox$applyRequirements$success);
      that.once('reject', sandbox$applyRequirements$fail);

      that.requirementTimeout = dcp_timers.setTimeout(function sandbox$applyRequirements$timeout() {
        that.off('applyRequirementsDone', sandbox$applyRequirements$success);
        that.off('reject', sandbox$applyRequirements$fail);
        reject(new Error(`applyRequirements never received 'applyRequirementsDone' response from sandbox: ${that.identifier}`));
      }, that.generalTimeoutMs);
      // Allow workers and localExec to exit.
      that.requirementTimeout.unref();

      const message = {
        webgpu: that.job.needCapabilities & capabilities.environment.webgpu,
        offscreenCanvas: that.job.needCapabilities & capabilities.environment.offscreenCanvas,
        worktime: that.job.worktime?.name,
        request: 'applyRequirements',
      };
      that.postMessageToEvaluator(message);
    });
  }

  /**
   * Assign job to the evaluator.
   * @returns {Promise<any>} - resolves on success, rejects otherwise
   */
  assignEvaluator ()
  {
    debugging('sandbox') && console.debug('Begin assigning job to evaluator', this.identifier);
    const that = this;

    return new Promise(function sandbox$$assignEvaluatorPromise(resolve, reject) {
      function sandbox$assignEvaluator$success(event)
      {
        that.off('reject', sandbox$assignEvaluator$fail);
        debugging('sandbox') && debug('Job assigned to evaluator');
        resolve(event);
      }
      function sandbox$assignEvaluator$fail(error)
      {
        that.off('assigned', sandbox$assignEvaluator$success);
        selectiveDebug() && console.debug('assignEvaluator failed', that.identifier, error);
        reject(error);
      }
      // Emitted by handleRing2Message.
      that.once('assigned', sandbox$assignEvaluator$success);
      that.once('reject', sandbox$assignEvaluator$fail);

      // Had to add useStrict -- not sure if anything else was missed.
      const jobMessage = {
        id:             that.job.jobId,
        arguments:      that.job.arguments,
        dependencies:   that.job.dependencies,
        modulePath:     that.job.modulePath,
        public:         that.job.public,
        requirePath:    that.job.requirePath,
        workFunction:   that.job.workFunction,
        useStrict:      that.job.useStrict,
        worktime:       that.job.worktime,
      };

      const message = {
        request: 'assign',
        job: jobMessage,
        sandboxConfig: that.hive.sandboxConfig,
      };
      that.postMessageToEvaluator(message);
    });
  }

  // _Idx
  //
  // eval, resetState, work, resetProgressTimeout, resetSliceTimeout
  //

  /**
   * Evaluates a string inside the sandbox.
   * @todo XXXpfr -- Currently unused, but will be useful for appliances!
   *
   * @param {string} code     - the code to evaluate in the sandbox
   * @param {string} filename - the name of the 'file' to help with debugging,
   * @returns {Promise<any>}  - resolves with eval result on success, rejects otherwise
   */
  eval (code, filename)
  {
    debugging('sandbox') && console.debug('Sandbox.eval', code, filename, this.identifier);
    const that = this;
    const msgId = nanoid();

    return new Promise(function sandbox$$eval$Promise(resolve, reject) {
      const eventId = EVAL_RESULT_PREFIX + msgId;
      function sandbox$eval$success(event)
      {
        that.off('reject', sandbox$eval$fail);
        resolve(event);
      }
      function sandbox$eval$fail(error)
      {
        that.off(eventId, sandbox$eval$success);
        reject(error);
      }
      that.once(eventId, sandbox$eval$success);
      that.once('reject', sandbox$eval$fail);

      const message = {
        request: 'eval',
        data: code,
        filename,
        msgId,
      };
      that.postMessageToEvaluator(message);
    });
  }

  /**
   * Resets the state of the bootstrap, without resetting the sandbox function if assigned.
   * Mostly used to reset the progress status before reusing a sandbox on another slice.
   * Must be called after @start.
   *
   * @returns {Promise<void>} - resolves with result on success, rejects otherwise
   */
  resetState ()
  {
    debugging('sandbox') && console.debug('Sandbox.resetState', this.identifier);
    if (this.isTerminated)
      return; // Silently bail.
    assert(this.isReserved); // Design assumption.

    const that = this;
    return new Promise(function sandbox$resetStatePromise(resolve, reject) {
      function sandbox$resetState$success ()
      {
        that.off('reject', sandbox$resetState$fail);
        dcp_timers.clearTimeout(that.resetStateTimeout);
        resolve();
      }
      function sandbox$resetState$fail(error)
      {
        that.off('resetStateDone', sandbox$resetState$success);
        dcp_timers.clearTimeout(that.resetStateTimeout);
        selectiveDebug() && console.debug('resetState failed', that.identifier, error);
        reject(error);
      }
      // Ring3
      that.once('resetStateDone', sandbox$resetState$success);
      that.once('reject', sandbox$resetState$fail);

      that.resetStateTimeout = dcp_timers.setTimeout(function sandbox$resetState$timeout() {
        that.off('resetStateDone', sandbox$resetState$success);
        that.off('reject', sandbox$resetState$fail);
        reject(new Error(`resetState never received resetStateDone event from sandbox: ${that.identifier}`));
      }, that.generalTimeoutMs);
      // Allow workers and localExec to exit.
      that.resetStateTimeout.unref();

      const message = {
        request: 'resetState',
      };
      that.postMessageToEvaluator(message);
    });
  }

  /**
   * Executes a slice received from the supervisor.
   * Must be called after this.start, this.assign and this.markAsWorking .
   * Sandbox.work will not terminate the sandbox upon failure.
   * The sandbox will be terminated in Supervisor.handleSandboxWorkError .
   * @returns {Promise<any>} - resolves with result on success, rejects otherwise
   */
  async work ()
  {
    const that = this;
    // Memoize slice for error reporting.
    const _slice = this.slice;

    // Clearly a slice is needed here and it should have been set via the call to Sandbox.assign(slice).
    // Please refer to the comments at the beginning of Sandbox.assign for more information.
    if (!this.slice) // Design assumption
      throw new Error(`Must have valid slice in sandbox before work can be done: ${this.identifier}`);

    await this.supervisor.delayManager.nextDelay('sandboxWork');
    selectiveDebug2() && console.debug('Sandbox.work begin', this.identifier, Date.now() - this.supervisor.lastTime);

    await this.resetState();
    // cf. DCP-1719,1720
    this.resetSliceReport();

    // Check that sandbox and slice have the same job.
    if (this.jobId !== this.slice.jobId)
      throw new Error(`Sandbox.work: sandbox ${this.identifier} and slice ${this.slice.identifier} are from different jobs`);
    // When evaluator goes down, all sandboxes are terminated.
    if (this.isTerminated)
    {
      selectiveDebug() && console.warn('Sandbox has been terminated in sandbox.work()');
      this.slice.resetFromReserved();
      return false;
    }

    // Mark sandbox and slice as working.
    this.slice.markAsWorking();
    this.markAsWorking();

    const { datum: inputDatum, error: dataError } = this.slice;
    if (dataError)
      that.postWorkEmit('error', dataError);

    return new Promise(function sandbox$$workPromise(resolve, reject) {
      function sandbox$$work$success (event)
      {
        that.removeListener('reject', sandbox$$work$fail);
        resolve(event);
      }

      function sandbox$$work$fail (error)
      {
        that.removeListener('resolve', sandbox$$work$success);
        reject(error);
      }

      that.once('resolve', sandbox$$work$success);
      that.once('reject', sandbox$$work$fail);

      that.slice.startTime = Date.now();
      that.progress = null;
      that.progressReports = {
        last: undefined,
        lastDeterministic: undefined,
      };

      that.resetProgressTimeout();
      that.resetSliceTimeout();
       /** @todo Should sliceHnd just be replaced with { sandbox: this } since 'this.public' is part of 'this'? */
      that.emit('start', { job: that.public, sandbox: that });
      that.sandboxHandle.emit('slice', that.slice.sliceNumber);

      if (dataError)
      {
        that.removeListener('resolve', sandbox$$work$success);
        that.removeListener('reject', sandbox$$work$fail);
        dcp_timers.setTimeout(() => reject(dataError), 0).unref();
      }
      else
      {
        // A GPU slice is beginning to execute on a sandbox. Track concurrent GPU slices; cf. DCP-4429
        if (that.jobManager.hasGPU)
        {
          that.gpuBarrier = ++that.supervisor.existingGPUBarriers;
          assert(that.gpuBarrier > 0 && that.gpuBarrier < 16);
          selectiveSupEx() && console.debug(`Starting work on GPU slice ${that.slice.sliceNumber} of GPU job ${that.jobId} in sandbox ${that.id}.`);
        }
        // Do the work.
        const message = { request: 'main', data: inputDatum, };
        that.postMessageToEvaluator(message, true /*recoverSlice*/);
      }
    })
    .then(async function sandbox$$work$then(result) {
      try // Make sure then-handler does not throw.
      {
        selectiveDebug() && console.debug(`Sandbox.work(endEventFired=${that.endEventFired}) has finished: ${that.identifier}`, result?.timeReport);
        // Tell supervisor that the slice is done working and is no longer occupying core space.
        that.slice.markAsWorkDone();
        // Transition slice to COMPLETE -- must happen before updateStatistics.
        that.slice.collectResult(result, true /*success*/);
        // Collect statistics and fire events -- must happen before checkSandboxReUse.
        that.jobManager.updateStatistics(that.slice, that.sandboxHandle);
        // The sliceEnd event fires immediately after the metrics event (in updateStatistics.)
        that.sandboxHandle.emit('sliceEnd', that.slice?.sliceNumber);
        // Clear slice property.
        that.slice = null;
        // Allow reuse of sandbox (WORKING ==> ASSIGNED) up to a limit of dcpConfig.supervisor.sandbox.maxSandboxUse .
        that.checkSandboxReUse();
      }
      catch (thenHandlerError)
      {
        selectiveSupEx() && console.debug(`Sandbox.work ${that.identifier} with slice ${_slice.identifier}: then-handler caught error:`, thenHandlerError);
        // When the sandbox isn't terminated, we end up here due to bugs, so make noise.
        if (!that.isTerminated)
          that.supervisor.directFatalError(thenHandlerError, `Unexpected failure of ${_slice.identifier} in sandbox.work result handling ${that.identifier}`);
        // Throw to go down the supervisor.handleSandboxWorkError code path.
        throw thenHandlerError;
      }

      that.clearTimers();
      // The 'complete' event triggers fetchTask so checkSandboxReUse must happen before,
      // in order to free up the current sandbox for reuse, when possible.
      that.emit('complete', that.jobId);

      return result;
    })
    .catch(async function sandbox$$work$catch(error) {
      // DANGER: Never introduce code here that could throw, except for the explicit throws below.
      selectiveDebug2() && console.debug(`Sandbox.work-catch ${that.identifier} failed to execute slice ${_slice.identifier}`, error);
      // Current sandbox will not be reused.
      // Do not overwrite that.slice because it is needed in subsequent error reporting.

      try
      {
        // Try what should be correct.
        _slice.markAsWorkDone(); // Tell supervisor the slice is no longer occupying core space.
      }
      catch (synchError)
      {
        // End up here due to bugs, so force WORKDONE.
        _slice.forceWorkDone();
      }

      that.clearTimers();

      selectiveDebug() && console.debug(`Sandbox.work ${that.identifier} throwing exception:`, error);

      if (!that.slice) // Possible when checkSandboxReUse throws.
        that.slice = _slice;
      throw error;
    })
    .finally(() => {
      // Track concurrent GPU slices; cf. DCP-4429
      if (that.gpuBarrier)
      {
        that.supervisor.existingGPUBarriers--;
        assert(this.gpuBarrier > 0 && this.gpuBarrier < 16);
        that.gpuBarrier = 0;
      }
    });
  }

  static clearTimer (timer)
  {
    if (timer)
      dcp_timers.clearTimeout(timer);
    return null;
  }

  clearTimers ()
  {
    this.progressTimer      = Sandbox.clearTimer(this.progressTimer);
    this.sliceTimeout       = Sandbox.clearTimer(this.sliceTimeout);
    this.describeTimeout    = Sandbox.clearTimer(this.describeTimeout);
    this.requirementTimeout = Sandbox.clearTimer(this.requirementTimeout);
    this.resetStateTimeout  = Sandbox.clearTimer(this.resetStateTimeout);
  }

  /**
   * Here's how we detect whether the underlying CPU is in a lower power state and compensate.
   * supervisor.checkForTabSuspension is placed on a 5 second interval timer and when in a low
   * power state the 5 second interval will take at least twice as long -- we detect that and set
   *    supervisor.detectedTabSuspended = true
   * which is set back to false by a timer with timespan this.hive.progressTimeoutMs + 1000.
   * If detectedTabSuspended = true happens when the resetProgressTimeout is already running,
   * then tabSuspendedSinceLastProgressEvent will be false, but we also check for detectedTabSuspended
   * and ENOPROGRESS is supressed.  If detectedTabSuspended = true happens 2 seconds before
   * resetProgressTimeout is called, then tabSuspendedSinceLastProgressEvent is true and will stay
   * true until resetProgressTimeout is called with detectedTabSuspended = false -- thus supressing
   * ENOPROGRESS again.
   */
  resetProgressTimeout()
  {
    Sandbox.clearTimer(this.progressTimer);
    this.tabSuspendedSinceLastProgressEvent = this.supervisor.detectedTabSuspended;
    const that = this;
    this.progressTimer = dcp_timers.setTimeout(function sandbox$ProgressTimeout() {
      if (that.options.ignoreNoProgress)
        return that.warning('ENOPROGRESS silenced by localExec: In a remote worker, this slice would be stopped for not calling progress frequently enough.');
      if (that.tabSuspendedSinceLastProgressEvent || that.supervisor.detectedTabSuspended)
        return that.warning('ENOPROGRESS ignored due to tab suspension.');

      that.emitNoProgress(`No progress event was received in the last ${that.hive.progressTimeoutMs / 1000} seconds.`);
    }, this.hive.progressTimeoutMs * timeDilation);
    // Allow workers and localExec to exit.
    this.progressTimer.unref();
  }

  /**
   * @param {string} msg -- no progress message
   */
  emitNoProgress (msg)
  {
    const error = new NoProgressError(msg);
    // Show error to job client
    const payload = {
      message:   error.message,
      code:      error.errorCode,
      timestamp: Date.now() - this.sliceStartTime,
    };
    this.postWorkEmit('error', payload);
    this.postWorkEmit('noProgress', { ...payload, progressReports: this.progressReports} );
    // Throw exception to the sandbox.work catch-handler.
    this.emit('reject', error);
  }

  /**
   * Plan
   * -----------------------------------------------------------------------------------------------
   * Need to compensate for CPU low power states, since SliceTooSlowError fires after 24 hours.
   * Identify time intervals where we're running slow (tab-suspended etc.) and only count them as
   * 25% of the true time interval (the 25% is a WAG, could refine a little with some calibration.)
   * Suppose the SliceTooSlowError is ready to fire, but 10 hours of the 24 hours is running at 25%,
   * then we'd cancel the timer and start a new one to compensate for the slow-time, which in this
   * case would allow another 7.5 hours before SliceTooSlowError fires.
   * -----------------------------------------------------------------------------------------------
   * Don't implement yet.  We need to do some thinking about a really good general solution.
   * (Paul, Severn)
   */
  resetSliceTimeout()
  {
    Sandbox.clearTimer(this.sliceTimeout);

    const that = this;
    this.sliceTimeout = dcp_timers.setTimeout(function sandbox$SliceTimeout() {
      if (Sandbox.debugWork)
        return that.warning('Sandbox.debugWork: Ignoring slice timeout');

      const error = new SliceTooSlowError(`Slice took longer than ${that.hive.sliceTimeoutMs / 1000} seconds.`)
      // Show error to job client
      const payload = {
        message:   error.message,
        code:      error.errorCode,
        timestamp: Date.now() - that.sliceStartTime,
      };
      that.postWorkEmit('error', payload);
      // Throw exception to the sandbox.work catch-handler.
      that.emit('reject', error);
    }, this.hive.sliceTimeoutMs * timeDilation);
    // Allow workers and localExec to exit.
    this.sliceTimeout.unref();
  }

  /**
   * Send payload to the workEmit endpoint in the event router.
   * @param {string} eventName
   * @param {*}      payload
   */
  postWorkEmit (eventName, payload)
  {
    selectiveDebug2() && console.debug('postWorkEmit:', eventName, stringify(payload));
    // Need to check if the sandbox hasn't been assigned a slice yet.
    if (!this.slice)
      return this.emitFatalError('Sandbox not assigned a slice before sending workEmit message to scheduler');
    return this.supervisor.postWorkEmit(this.slice, eventName, payload);
  }

  /**
   * Save rejected slice timeReport data in this.slice.rejectedState.
   * @param {{ reason: string|false, retries: number }} data
   * @return {{message: string|false, retries: number, name: string}} -- work.reject info
   */
  handleRejectedWork (data)
  {
    selectiveSupEx() && console.debug('handleRejectedWork', data, this.identifier);
    assert(this.slice);
    return this.slice.constructRejectedState(data, this.sliceTimeReport, this.inDataSize, this.outDataSize);
  }

  /**
   * Attach CGIO to result returned by a slice workFn.
   * @param {*} completeData - results
   */
  attachCGIOToResult (completeData)
  {
    if (!completeData)
      throw new Error(`Sandbox ${this.identifier} result is not ready`); // Should never fire.
    if (completeData['timeReport'])
      throw new Error(`Sandbox ${this.identifier} result already has timeReport`); // Should never fire.
    if (completeData['dataReport'])
      throw new Error(`Sandbox ${this.identifier} result already has dataReport`); // Should never fire.
    // Can fire in rare races.
    // If a reject event fires while sandbox.work() is running, the reject handler will remove the
    // resolve event handler, which can cause the following error to fire. It's a harmless race.
    if (common.highPriorityDiag(false) && !(this.listenerCount('resolve') > 0))
      this.supervisor.directError(new Error(`Sandbox ${this.identifier} is in an unexpected state (there are no resolve event handlers)`));

    completeData['timeReport'] = this.sliceTimeReport;
    completeData['dataReport'] = {
      InDataSize:  this.inDataSize,
      OutDataSize: this.outDataSize,
    };
    // Is there rejectedState in the slice from being rejected once when reason := false ?
    if (this.slice.rejectedState)
    {
      common.accumulateTime(completeData['timeReport'], this.slice.rejectedState.report);
      completeData['dataReport'].InDataSize  += this.slice.rejectedState.report.InDataSize;
      completeData['dataReport'].OutDataSize += this.slice.rejectedState.report.OutDataSize;
    }
    selectiveDebug() && console.debug(`attachCGIOToResult: sliceNumber=${this.sliceNumber}, job=${this.jobId}`, completeData);
  }

  // _Idx
  //
  // handleRing0Message, handleRing1Message, handleRing2Message, handleRing3Message
  //

  async handleRing0Message(data) // eslint-disable-line require-await
  {
    debugging('ring0') && console.debug('Ring0', this.identifier, data.request);

    switch (data.request)
    {
      // The private '__sandboxLoaded' event is used by dcp-native; do not remove.
      // Otherwise, do not use __sandboxLoaded.
      case '__sandboxLoaded': // SAVE
        break;
      case 'scriptLoaded':
        if(data.result !== "success")
          this.onerror(data);
        break;
      case 'error':
        selectiveSupEx() && console.debug('Sandbox error in ring0', Boolean(this.slice), data.error);
        this.rejectWithCleanup('during initialization', data.error, true /*isRing0*/);
        break;
      case 'unrecoverable-evaluator':
        this.warning('DCP-Evaluator unrecoverable error: ' + (typeof data.message === 'string' ? data.message : JSON.stringify(data.message)));
        this.rejectWithCleanup('unrecoverable error', data.error, true /*isRing0*/);
        break;
      default:
        this.emitFatalError(`Received unhandled request from sandbox ring 0. Data: ${stringify(data)}`);
        break;
    }
  }

  async handleRing1Message(data) // eslint-disable-line require-await
  {
    debugging('ring1') && console.debug('Ring1', this.identifier, data.request);

    switch (data.request)
    {
      case 'applyRequirementsDone':
        // emit internally
        this.emit(data.request, data)
        break;
      default:
        this.emitFatalError(`Received unhandled request from sandbox ring 1. Data: ${stringify(data)}`);
        break; 
    }
  }

  async handleRing2Message(data)
  {
    debugging('ring2') && console.debug('Ring2', this.identifier, stringify(data, -1, 2));

    // Ring 2 response to 'eval' request is distinct: the request contains a nanoid
    // to match it back with the eval call that triggered it 
    if ( /evalResult::/.test(data.request))
      return this.emit(data.request, data);

    switch (data.request)
    {
      case 'dependency': {
        // Save the current slice
        const currentSlice = this.slice;
        try
        {
          // It possible that a DCP package dependency is being fetched right before the slice
          // completes.  This 'race' means that this.slice may have changed to null or another slice
          // by the time an exception happens.  This is a bug in the design of Sandbox and the bravojs
          // code in DCP-CLIENT repro.
          const moduleData = await this.moduleCache.fetchModule(data.data, this.jobId);
          // moduleData will be null if the module exists, but fetching the data within it failed.
          // This could be a transient error that may happen within the worker due to networking,
          // kill the sandbox & return the slice, but be willing to try again
          if (moduleData === null)
          {
            this.returnSlice('EMODULEFECTCH');
            this.terminate(false);
            return;
          }
          // Module data is dynamic since it may only be required in a conditional branch.
          // Moreover, on a long job, the published module itself may be updated on the scheduler.
          let moduleLength = 0;
          for (const moduleGroup of Object.values(moduleData))
            for (const file of Object.values(moduleGroup))
              moduleLength += file.length;
          this.moduleInDataSize += moduleLength;
          selectiveDebug() && console.debug('Sandbox.Ring2.fetchModule size', this.moduleInDataSize, moduleLength);
          // Send module data to be evaluator.
          const message = {
            request: 'moduleGroup',
            data: moduleData,
            id: data.id,
          };
          this.postMessageToEvaluator(message);
        }
        catch (error)
        {
          // If this.slice !== currentSlice, the dependency being fetched was (hopefully) not really needed,
          if (this.slice === currentSlice)
          {
            // When the module is too large, usually in browsers with insufficient memory, the error comes back as
            //   'RangeError: Invalid array length'. Convert into more meaningful error.
            if (error.code === 'DCPC-1024')
              error.code = 'EMODULETOOBIG';
            // When readFile cannot open a stream, convert into more meaningful error.
            else if (error.code === 'ENOENT')
              error.code = 'ENOMODULE';

            // Both 'EMODULETOOBIG' and 'ENOMODULE' are fatal errors.
            if (error.code === 'EMODULETOOBIG' || error.code === 'ENOMODULE')
              this.supervisor.rejectJob(this.jobManager, error.code);

            // Only do workEmit on a valid this.slice becaue we need to authorization message.
            // In order to let the job client know that fetchModule fails, we need to use the event
            // router workEmit protocol which requires validation of a security token which is attached
            // to every slice: authorizationMessage. This security token is necessary for DCP daemons
            // to allow a request to be processed.
            if (this.slice && error.code !== 'EMODULETOOBIG')
              this.postWorkEmit('error', error);

            selectiveSupEx() && console.debug(`Sandbox.Ring2: fetchModule failed ${this.identifier}`, error);
            this.emit('reject', error);
          }
        }
        break;
      }
      case 'error':
        /*
         * Ring 2 error messages will only fire for problems inside of the worker that are separate from
         * the work function. In most cases there are other handlers for situations where 'error' may be emitted
         * such as timeouts if the expected message isn't received.
         */
        debug('Sandbox error in ring2', data.error);
        this.rejectWithCleanup('during assignment and dependency resolution', data.error);
        break;
      case 'describe':
      case 'resetStateDone':
      case 'assigned':
        this.emit(data.request, data); // emit internally
        break;
      case 'fetchRequest': {
        /** @todo Factor this case into a function call. */
        const id = data.id;
        const reqObj = data.data;

        const isAllowed = this.supervisor.originManager.isAllowed(reqObj.url, 'workerFetch', null);
        if (!isAllowed)
        {
          const err = new DCPError(`Not allowed to fetch from '${reqObj.url}'`, 'EPERM_ORIGIN');
          this.postMessageToEvaluator({
            request: 'fetchError',
            id,
            error: {
              name: err.name,
              message: err.message,
              stack: err.stack,
              code: err.code,
              timestamp: err.timestamp,
            },
          });
          break;
        }
        
        /**
         * Measure the total length of HTTP headers as it's in a raw HTTP message
         * @param {string[][]} headersEntries entries in the form of `[string, string][]`
         */
        function measureHeadersLength(headersEntries) {
          return headersEntries.map(
            ([headerName, headerValue]) => `${headerName}: ${headerValue}`
          ).join('\r\n')
           .length;
        }

        /** @type {Response} */
        let res;
        /** @type {Uint8Array | null} */
        let resBody;
        try
        {
          // output byte measurement
          this.fetchOutDataSize += `${reqObj.method} ${reqObj.url}\r\n`.length; // mimics the start-line in a raw HTTP request message, like `GET http://example.com/path/`
          this.fetchOutDataSize += measureHeadersLength(reqObj.headersEntries); // mimics the headers in a raw HTTP message
          this.fetchOutDataSize += 4; // \r\n\r\n
          if (reqObj.bodyBuffer) this.fetchOutDataSize += reqObj.bodyBuffer.byteLength;

          // deserializeRequest
          const req = new Request(reqObj.url, {
            method: reqObj.method,
            headers: new Headers(reqObj.headersEntries),
            body: reqObj.bodyBuffer,
        
            mode: reqObj.mode || 'cors',
            credentials: reqObj.credentials,
          }); // may error when re-constructing the Request
          // See https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch#exceptions
          
          // do fetch
          res = await fetch(req);
          resBody = res.body ? new Uint8Array(await res.arrayBuffer()) : null; // may error when retrieving the response body
        }
        catch (err)
        {
          this.postMessageToEvaluator({
            request: 'fetchError',
            id,
            error: {
              name: err.name,
              message: err.message,
              stack: err.stack,
              code: err.code,
              timestamp: err.timestamp,
            },
          });
          break;
        }

        // serializeResponse
        const resObj = {
          url: res.url,
          status: res.status,
          statusText: res.statusText,
          headersEntries: [...res.headers.entries()],
          bodyBuffer: resBody,
        };

        // input byte measurement
        this.fetchInDataSize += `${resObj.status} ${resObj.statusText}\r\n`.length // mimics the start-line in a raw HTTP response message
        this.fetchInDataSize += measureHeadersLength(resObj.headersEntries);
        this.fetchInDataSize += 4; // \r\n\r\n
        if (resObj.bodyBuffer) this.fetchInDataSize += resObj.bodyBuffer.byteLength;

        this.postMessageToEvaluator({
          request: 'fetchResponse',
          id,
          data: resObj,
        });
        break;
      }
      case 'reject':
        this.rejectWithCleanup('rejected', data.error);
        break;
      default:
        this.emitFatalError(`Received unhandled request from sandbox ring 2. Data: ${stringify(data)}`);
        break;
    }
  }

  async handleRing3Message(data) // eslint-disable-line require-await
  {
    debugging('ring3') && console.debug('Ring3', this.identifier, data.request);

    switch (data.request)
    {
      case 'complete':
        this.clearTimers();
        if (this.progress === null)
        {
          if (this.options.ignoreNoProgress)
            this.warning("ENOPROGRESS check disabled: progress() was not called during this slice's execution; this would normally cause the slice to fail.");
          else
          {
            // If a progress update was never received (progress === null) then reject
            this.emitNoProgress('Sandbox never emitted a progress event.');
            break;
          }
        }
        
        this.progress = 100;
        this.sliceOutDataSize += kvin.stringify(data.result).length; /** @todo - fix per DCP-3750 */
        this.attachCGIOToResult(data);
        // Resolve the work promise
        this.emit('resolve', data);
        break;
      case 'progress':
      {
        const { progress, indeterminate, throttledReports, value } = data;
        this.progress = progress;
        // cf. job-noProgress.js
        const progressReport = {
          deltaMs: Date.now() - this.sliceStartTime,
          progress,
          value,
          throttledReports,
        }
        this.progressReports.last = progressReport;
        if (!indeterminate)
          this.progressReports.lastDeterministic = progressReport;

        this.resetProgressTimeout();
        this.sandboxHandle.emit('progress', indeterminate || progress < 0 || progress > 100 ? undefined : progress);
        break;
      }
      case 'noProgress':
        this.emitNoProgress(data.message);
        break;
      case 'console':
        data.payload.message = this.jobManager.serializer.marshal(data.payload.message);
        this.sliceOutDataSize += JSON.stringify(data.payload.message).length; /** @todo - fix per DCP-3750 */
        this.postWorkEmit('console', data.payload);
        break;
      case 'emitEvent': /* ad-hoc event from the sandbox (work.emit) */
        this.postWorkEmit('custom', data.payload);
        break;
      case 'measurement':
        this.updateTime(data);
        break;
      case 'sandboxError': /* the sandbox itself has an error condition */
        selectiveSupEx() && console.debug(`Ring3 received a 'sandboxError' event for sandbox ${this.identifier}`, data);
        this.emit('sandboxError', data.error);
        this.rejectWithCleanup('internal sandbox error while executing work function', data.error);
        break;
      case 'workError': /* the work function threw/rejected */
        selectiveSupEx() && console.debug(`Ring3 received a 'workError' event for sandbox ${this.identifier}`, data.error);
        const uncaughtError = new UncaughtExceptionError(data.error);
        this.postWorkEmit('error', uncaughtError);
        // Implementation of workFn-errors-as-results.
        const sandboxWorkResult = { result: uncaughtError };
        this.sliceOutDataSize += kvin.stringify(uncaughtError).length; /** @todo - fix per DCP-3750 */
        this.attachCGIOToResult(sandboxWorkResult);
        this.emit('resolve', sandboxWorkResult);
        break;
      case 'workReject': /* Syntax: work.reject(stringReason, retries = 0) */
        selectiveSupEx() && console.debug(`Ring3 received a 'workReject' event for sandbox ${this.identifier}`, stringify(data));
        const workRejectData = data.workRejectData;
        const workRejectInfo = this.handleRejectedWork(workRejectData);
        /**
         * Reject with workRejectInfo to stop execution.
         * XXXpfr @todo Hook up to the StandAloneWorker die() command, instead of rejecting and going through the error-path
         */ 
        this.rejectWithCleanup('reject handler not available when processing work.reject', workRejectInfo);
        break;
      default:
        this.emitFatalError(`Received unhandled request from sandbox ring 3. Data: ${stringify(data)}`);
        break; 
    }
  }

  /**
   * Try to send the error back to the reject handler in Sandbox.work.
   * But if the reject handler is not available (s.b. rare) then cleanup, emit error and throw.
   * @param {string}  message
   * @param {{ message }}   error
   * @param {boolean} [isRing0]
   */
  rejectWithCleanup (message, error, isRing0 = false)
  {
    if (this.listenerCount('reject') > 0)
      this.emit('reject', error);
    else
    {
      // Should never get here.
      if (!isRing0)
        this.terminate(false);
      this.supervisor.directFatalError(error, `Sandbox ${this.identifier} ${message}`);
      throw Diagnostics.doNotEmitError(error);
    }
  }

  // _Idx
  //
  // onmessage, onerror, terminate
  //

  /**
   * Handles progress and completion events from sandbox.
   * Unless explicitly returned out of this function will re-emit the event
   * where the name of the event is event.data.request.
   *
   * @param {object} event - event received from the evaluator sandbox
   * @returns {Promise<void>}
   */
  onmessage (event)
  {
    debugging('event') && debug('onmessage-event', event.data.ringSource);
    if (Sandbox.debugEvents)
      console.debug('sandbox - eventDebug:', { id: this.id, state: this.state.valueOf(), event: stringify(event) });

    const { data } = event;
    const ringLevel = data?.ringSource

    // Give the data to a handler depending on ring level
    if (ringLevel === -1)
    {
      /** @todo XXXpfr Beware of this happening. */
      this.terminate(true);
      this.emitFatalError(`Message sent directly from raw postMessage: ${stringify(event)}`);
    }
    else
    {
      const handler = this.ringMessageHandlers[ringLevel];
      if (handler)
        return handler.call(this, data.value).catch((error) => {
          this.supervisor.directFatalError(error, `Sandbox.onmessage failed in ring${ringLevel}`);
          throw error;
        });
      this.warning(`No handler defined for message from ring ${ringLevel} for event ${stringify(event)}`);
    }
  }

  /**
   * Error handler for the internal sandbox.
   * Emits error event that gets handled up in the Worker class.
   */
  onerror (event)
  {
    /** @todo XXXpfr Beware of onerror firing. */
    this.terminate(true);
    this.emitFatalError(`Sandbox.onerror fired with event: ${stringify(event)}`);
  }

  /**
   * Terminates the sandbox, clears timeouts and emits a reject event when reject is true.
   * The only way to terminate a sandbox is through this function.
   *
   * @param {boolean|Error} reject - if (reject instanceof Error) emit 'reject' event with arg reject, otherwise when true, uses a default Error.
   * @returns {boolean} - true when sandbox is terminated; false when delayed by backoffManager
   */
  terminate (reject)
  {
    if (this.isTerminated)
      return true;

    try
    {
      // Don't call sandbox.terminate(false) when inside sandbox.start().
      // Instead save such sandboxes and terminate them in the finally clause of sandbox.start().
      this.backoffManager.terminateCriticalRegion = true;
      if (!reject && this.backoffManager.createCriticalRegion)
      {
        this.backoffManager.pendingTerminate.push(this);
        return false;
      }

      // Leave this.slice intact for debugging purposes.
      selectiveDebug() && console.debug(`Terminate sandbox ${this.identifier}`, this.supervisor.sandboxInventory.map((sbx) => sbx.identifier));

      // if (!reject && this.isWorking) -- Not ready for this; too crude
      //   reject = true;

      this.state.set([ UNREADY, READYING, READY, ASSIGNING, ASSIGNED, RESERVED, WORKING ], TERMINATED);

      this.clearTimers();

      if (this.evaluatorHandle && typeof this.evaluatorHandle.terminate === 'function')
      {
        try
        {
          this.evaluatorHandle.terminate();
        }
        catch (e)
        {
          this.supervisor.directError(e, `Error terminating sandbox ${this.id}:`);
        }
        finally
        {
          this.evaluatorHandle = null;
        }
      }

      // This is the normal place to remove sandboxes from inventory.
      common.removeElement(this.supervisor.sandboxInventory, this);

      if (common.displayMaxDiagInfo())
        this.sandboxHandle.emit('end', this.endEventFired);
      else
        this.sandboxHandle.emit('end');

      if (reject)
        this.emit('reject', (reject instanceof Error) ? reject : new Error(`Sandbox ${this.identifier} was terminated.`));

      return true; // Successfully terminated
    }
    finally
    {
      this.backoffManager.terminateCriticalRegion = false;
    }
  }

  // _Idx
  //
  // updateTime, resetSliceReport, error, warning
  //

  /**
   * Ring3 can send a `measurement` request and whence update the time report.
   * @param {{ total: number, CPU: number, webGL: number, webGPU: number }} measurementEvent
   */
  updateTime (measurementEvent)
  {
    common.accumulateTime(this.sliceTimeReport, measurementEvent);
  }

  /**
   * Start over sandbox work timers.
   */
  resetSliceReport ()
  {
    this.sliceTimeReport = {
      total: 0,
      CPU: 0,
      webGL: 0,
      webGPU: 0,
    };
    this.sliceOutDataSize = 0;
    this.fetchInDataSize = 0;
    this.fetchOutDataSize = 0;
  }

  /**
   * Error feedback to user.
   * @param {string} message
   * @param {*} [coreError=null]
   */
  emitFatalError (message, coreError)
  {
    this.returnSlice(message);
    this.supervisor.emitFatalError(message, coreError);
  }

  /**
   * Warning feedback to user.
   * @param {string[]} messages
   */
  warning (...messages)
  {
    this.supervisor.warning(...messages);
  }
}

Sandbox.idCounter = 1;
Sandbox.debugWork = false;
Sandbox.debugState = false;
Sandbox.debugEvents = false;

exports.Sandbox = Sandbox;
exports.SandboxError = SandboxError;
exports.NoProgressError = NoProgressError;
exports.SliceTooSlowError = SliceTooSlowError;
exports.UncaughtExceptionError = UncaughtExceptionError;
